import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/Loader';
import Paginate from 'components/Paginate';
import TableError from 'components/TableError';
import { COMMUNITY_MEMBER_SEARCH } from 'containers/community/graphql/community.query';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { ACTIVE_AND_DELETED_ARRAY } from 'utils/constants';
import { checkIfValueExist, printDate, printSerialNumbers, urlSlugify } from 'utils/helper';
import Select from 'react-select';
import confirm from 'reactstrap-confirm';
import { REMOVE_MEMBER_FROM_GROUP, UPDATE_MEMBER_FROM_GROUP } from 'containers/community/graphql/community.mutation';

const ContributorsList = () => {
  const router = useRouter();
  const { query } = router;
  const [membersList, setMembersList] = useState([]);
  const [membersPagination, setMembersPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    name: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getMembersRequest, { loading: communityMembersRequestLoading }] = useLazyQuery(COMMUNITY_MEMBER_SEARCH, {
    onCompleted: (data) => {
      const payload = data?.admin_community_member_search;
      if (!isEmpty(payload)) {
        setMembersList(payload?.results);
        setMembersPagination(payload?.pagination);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [markMemberActiveInActiveRequest, { loading: markMemberActiveLoading }] = useMutation(
    UPDATE_MEMBER_FROM_GROUP,
    {
      onCompleted: (data) => {
        if (!isEmpty(data?.admin_community_member_update)) {
          handleChangePage(parseInt(router?.query?.page, 10) || 1);
          toast.success('Success! Community Member has been Updated ');
        }
      },
      onError: (error) => {
        toast.error('Something went wrong!');
      },
    }
  );

  const [removeMemberRequest, { loading: removeMemberLoading }] = useMutation(REMOVE_MEMBER_FROM_GROUP, {
    onCompleted: (data) => {
      if (data?.admin_community_member_remove) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success('Success! Community Member has been Updated ');
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const handleChangePage = (page) => {
    getMembersRequest({
      variables: {
        searchDTO: {
          page: page ? page : 1,
          size: 20,
          groupId: query?.id,
          sortBy: 'joiningDate',
          sortOrder: -1,
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          name: urlSlugify(filters?.name),
        },
      },
    });
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = async () => {
    setFilters({ ...defaultFilters, name: '' });
    await getMembersRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          groupId: query?.id,
          sortBy: 'joiningDate',
          sortOrder: -1,
        },
      },
    });
  };

  useEffect(() => {
    if (!isEmpty(query?.id)) {
      handleChangePage();
    }
  }, [query?.id]);

  const handleMemberActive = async (data) => {
    // toggle member active
    await markMemberActiveInActiveRequest({
      variables: {
        memberId: data?.id,
        createDTO: {
          groupId: data?.group?.id,
          role: data?.role,
          memberId: data?.memberId,
          deleted: data?.deleted,
          active: !data?.active,
        },
      },
    });
  };

  const handleRemoveMember = async (data) => {
    const result = await confirm({
      title: `${data?.deleted ? 'Undelete' : 'Delete'} Member`,
      message: `Are you sure, you want to ${data?.deleted ? 'Undelete' : 'Delete'} this Member?`,
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });
    if (result) {
      if (data?.deleted) {
        await markMemberActiveInActiveRequest({
          variables: {
            memberId: data?.id,
            createDTO: {
              groupId: data?.group?.id,
              role: data?.role,
              memberId: data?.memberId,
              deleted: !data?.deleted,
              active: data?.active,
            },
          },
        });
      } else {
        await removeMemberRequest({
          variables: {
            memberId: data?.id,
          },
        });
      }
    }
  };

  return (
    <>
      <Loader isActive={communityMembersRequestLoading || removeMemberLoading || markMemberActiveLoading} />

      <Card>
        <CardBody>
          <div className="mt-4">
            <div className="row">
              <div className="col-lg-2 mb-4">
                <label className="form-label">Active</label>
                <Select
                  options={ACTIVE_AND_DELETED_ARRAY}
                  value={filters.active}
                  onChange={(value) => setFilters({ ...filters, active: value })}
                />
              </div>
              <div className="col-lg-2 mb-4">
                <label className="form-label">Deleted</label>
                <Select
                  options={ACTIVE_AND_DELETED_ARRAY}
                  value={filters.deleted}
                  onChange={(value) => setFilters({ ...filters, deleted: value })}
                />
              </div>

              <div className="col-lg-3 mb-4">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 pt-2">
            <a className="btn btn-primary me-4" onClick={applyFilters}>
              Apply Filters
            </a>
            <a className="" onClick={clearFilters}>
              Clear
            </a>
          </div>
        </CardBody>
      </Card>

      <Row className="mb-2 mt-4">
        <Col sm="4">
          <div className="search-box me-2 d-inline-block">
            <div className="position-relative font-bold">{membersPagination?.totalCount || 0} Members Found</div>
          </div>
        </Col>
      </Row>

      <div className="table-responsive mt-4" style={{ minHeight: 300 }}>
        <Table className="project-list-table table-centered table-borderless ">
          <thead>
            <tr className="table-warning">
              <th scope="col">S.No.</th>
              <th scope="col">Name</th>
              <th scope="col">Role</th>
              <th scope="col">Follower Count</th>
              <th scope="col">Following Count</th>
              <th scope="col">Post Count</th>
              <th scope="col">Reaction Count</th>
              <th scope="col">Comment Count</th>
              <th scope="col">Joining Date</th>
              <th scope="col">Active</th>
              <th scope="col">Deleted</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {!isEmpty(membersList) ? (
              membersList?.map((item, index) => {
                return (
                  <tr
                    key={`_test_${item?.memeber?.displayName}_`}
                    className={!item?.active || item?.deleted ? 'bg-danger' : ''}>
                    <td>{printSerialNumbers(membersPagination)[index]}</td>
                    <td>{item?.member?.displayName}</td>
                    <td>{item?.role}</td>
                    <td>{item?.stats?.followerCount}</td>
                    <td>{item?.stats?.followingCount}</td>
                    <td>{item?.stats?.postCount}</td>
                    <td>{item?.stats?.reactionCount}</td>
                    <td>{item?.stats?.commentCount}</td>
                    <td>{printDate(item?.joiningDate)}</td>
                    <td>
                      <Badge className={item?.active ? 'bg-success' : 'bg-danger'}>{item?.active ? 'Yes' : 'No'}</Badge>
                    </td>
                    <td>
                      <Badge className={item?.deleted ? 'bg-success' : 'bg-danger'}>
                        {item?.deleted ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td>
                      <UncontrolledDropdown>
                        <DropdownToggle href="#" className="card-drop" tag="i">
                          <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem onClick={() => handleMemberActive(item)}>
                            <i
                              className={`fas  ${
                                item?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                              }   m-1 pe-3`}
                            />
                            {item?.active ? 'Disable' : 'Enable'}
                          </DropdownItem>
                          <DropdownItem onClick={() => handleRemoveMember(item)}>
                            <i className={'fas fa-trash-alt text-danger m-1 pe-3'} />
                            {item?.deleted ? 'Undelete' : 'Delete'}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                );
              })
            ) : (
              <TableError />
            )}
          </tbody>
        </Table>

        {!isEmpty(membersList) && <Paginate pageInfo={membersPagination} onChangePage={handleChangePage} />}
      </div>
    </>
  );
};

export default ContributorsList;
